<template>
  <v-container class="mainContainerBackground">
    <!-- Debt Solutions -->
    <!-- Enter your debt information below... -->
    <v-row class="px-4">
      <v-col cols="12" class="font-weight-bold pb-0">
        <h2>{{ $t('L_debt_solution') }}</h2>
        <p class="my-4 text-body-1">{{ $t('L_enter_your_debt_information') }}</p>
        <p class="my-4 text-body-1">{{ $t('L_revisit_this_page_to') }}</p>
        <p class="my-4 text-body-1">{{ $t('L_in_order_to_provide_an_accurate') }}</p>
      </v-col>
    </v-row>

    <!-- Date Input -->
    <v-row class="px-4 pt-0">
      <v-col cols="12" sm="8" md="5" class="pt-0 pb-0">
        <v-form ref="submit_date_debt" id="submit_date_debt" v-model="validForm" autocomplete="off">
          <OnexDatePicker
            id="persondob"
            type="dob"
            v-model="persondob"
            :date-format="dobFormatLanguage"
            :label="$t('L_birth_date')"
            :min="minDateCheck"
            :max="maxDateCheck"
            :out-of-range-message="outOfRangeMessageText"
            required
            sensitive
          />
        </v-form>
      </v-col>
    </v-row>

    <v-row>
      <!-- My Current Debt Details -->
      <v-col cols="12" sm="12" md="8" class="pt-0 pb-0">
        <MyDebtDetails
          :disablebutton="!(validForm && persondob)"
          :tpersondob="persondob"
          @is-debt-input-hidden="setHideDebtInput"
        />
      </v-col>
      <!-- Current Debt Summary -->
      <v-col cols="12" sm="12" md="4" class="pt-0">
        <MyDebtSummary @call-contact-rep="bubbleUpContactRepRequest" @changeTab="callViewMyPlan" />
      </v-col>
    </v-row>
  </v-container>
</template>

<script lang="ts">
import { defineComponent, type ComponentOptions } from 'vue';
import { differenceInYears, format, parse } from 'date-fns';
import { mapActions, mapState } from 'pinia';
import { useDebtSolutionsStore } from '../store/debtsolutions.store';
import { useCustomerStore } from '@/stores/customer/customer.store';
import { usePolicyStore } from '@/modules/policy/store/policy.store';
import MyDebtSummary from './MyDebtSummary.vue';
import MyDebtDetails from './MyDebtDetails.vue';
import { minDateCheck, maxDateCheck } from '@/shared/utils/dateFormats';
import { useVuelidate } from '@vuelidate/core';
import { DateFormats, OnexDatePicker, UILocale } from '@primerica/onex-ui-lib';
import type { IPhase } from '@/modules/policy/models/Phase.ts';

const componentOptions: ComponentOptions = {
  name: 'MyDebtTab',
  components: {
    MyDebtDetails,
    MyDebtSummary,
    OnexDatePicker,
  },
  data: () => ({
    persondob: '',
    hideDebtInput: true,
    v$: useVuelidate(),
    validForm: false,
  }),
  computed: {
    ...mapState(useDebtSolutionsStore, {
      paidOffDebts: 'paidOffDebts',
      unpaidDebts: 'unpaidDebts',
      step: 'step',
      scenario: 'scenario',
      personInfo: 'personInfo',
    }),
    ...mapState(useCustomerStore, {
      investments: 'investments',
    }),
    ...mapState(usePolicyStore, {
      currentPolicy: 'policy/currentPolicy',
    }),
    isFrenchLocale() {
      if (this.locale?.length > 0) {
        if (this.locale.toLowerCase().indexOf('fr') > -1) {
          return true;
        }
        return false;
      }
      return false;
    },
    dobFormatLanguage() {
      return this.$i18n.locale === UILocale.FR_CA.toString()
        ? DateFormats.DDMMYYYY
        : DateFormats.MMDDYYYY;
    },
    outOfRangeMessageText() {
      const dobDate = new Date(this.persondob);
      if (dobDate >= this.maxDateCheck) {
        return this.$t('L_invalid_date_must_be_for_ages');
      } else if (dobDate <= this.minDateCheck) {
        return this.$t('L_invalid_date_must_be_for_ages');
      }
    },
    minDateCheck,
    maxDateCheck,
  },
  watch: {
    '$i18n.locale'(newLocale, oldLocale) {
      if (newLocale !== oldLocale) {
        let currentDob: Date | null = null;

        if (this.persondob instanceof Date) {
          currentDob = new Date(this.persondob);
        } else if (typeof this.persondob === 'string' && this.persondob.length > 0) {
          const sourceFormat =
            oldLocale?.toLowerCase().indexOf('fr') > -1 ? 'ddMMyyyy' : 'MMddyyyy';
          const parsedDob = parse(this.persondob, sourceFormat, new Date());

          if (!isNaN(parsedDob.getTime())) {
            currentDob = parsedDob;
          } else {
            const fallbackDob = new Date(this.persondob);
            if (!isNaN(fallbackDob.getTime())) {
              currentDob = fallbackDob;
            }
          }
        }

        if (currentDob && !isNaN(currentDob.getTime())) {
          // Re-assign as Date so the date picker re-renders using the new locale format.
          this.persondob = new Date(currentDob);
        }
      }
    },
    async 'persondob'(newData, oldData) {
      const value = newData;
      try {
        if (
          oldData !== newData &&
          !this.outOfRangeMessageText &&
          !isNaN(new Date(value).getTime())
        ) {
          const tDate = new Date(value);

          // Start of scenario
          // Add logic for grabbing this.scenario
          const tempScenario = JSON.parse(JSON.stringify(this.scenario));
          // set dob and currentage for this.scenario
          // Check for non null, not {}
          if (
            tempScenario &&
            Object.keys(tempScenario).length !== 0 &&
            tempScenario.constructor === Object &&
            tempScenario.personInfo != null &&
            tempScenario.personInfo.length > 0
          ) {
            tempScenario.personInfo[0].dob = format(tDate, "yyyy-MM-dd'T'00:00:00-00:00");
            tempScenario.personInfo[0].currentAge = differenceInYears(new Date(), tDate);
            this.setScenario(tempScenario);
          }
          // End of scneario

          // Start of this.personInfo
          let tempPersonInfo = null;
          if (this.personInfo != null) {
            tempPersonInfo = JSON.parse(JSON.stringify(this.personInfo));
          }
          if (
            tempPersonInfo &&
            Object.keys(tempPersonInfo).length !== 0 &&
            tempPersonInfo.constructor === Object
          ) {
            tempPersonInfo.dob = format(tDate, "yyyy-MM-dd'T'00:00:00-00:00");
            tempPersonInfo.currentAge = differenceInYears(new Date(), tDate);
          } else {
            tempPersonInfo = {
              personType: 'P',
              name: 'Primary Debt',
              currentAge: 0, // 46 Grab from  life or investment
              retirementAge: 0, // 67 Grab from  investment  else use default CA: 65 US: 67
              dob: '', // '1974-06-06T00:00:00' Grab from life or investment
              grossMonthlyIncome: 5000.0,
            };
            tempPersonInfo.dob = format(tDate, "yyyy-MM-dd'T'00:00:00-00:00");
            tempPersonInfo.currentAge = differenceInYears(new Date(), tDate);
          }
          this.personInfo = tempPersonInfo;
          // End of this.personInfo
          await this.pushDebts(this.persondob);
        }
      } catch (_error) {
        /* empty */
      }
    },
  },
  methods: {
    ...mapActions(useDebtSolutionsStore, {
      debtInit: 'debtInit',
      pushDebts: 'pushDebts',
      setScenario: 'setScenario',
    }),
    callViewMyPlan() {
      this.$emit('changeTab', 1);
    },
    bubbleUpContactRepRequest() {
      this.$emit('contact-your-rep', true);
    },
    setHideDebtInput(requestObject: boolean | null) {
      if (requestObject != null) {
        this.hideDebtInput = requestObject;
      }
    },
  },
  async mounted() {
    if (this.step === undefined || this.step === -1) {
      await this.debtInit();
    }

    // This is to update MyPlan data using the updated retirementAge
    if (
      this.investments?.calculator?.scenario?.retirement?.age !== this.personInfo?.retirementAge
    ) {
      await this.debtInit();
    }

    // Scenario as first source of truth
    if (this.scenario?.personInfo != null && this.scenario.personInfo.length > 0) {
      const personInfoDobCopy = JSON.parse(JSON.stringify(this.scenario.personInfo[0]?.dob));
      if (personInfoDobCopy != null) {
        this.persondob = new Date(personInfoDobCopy);
      }
    }

    // Investment as 2nd source of truth for dob
    if (
      this.investments?.calculator?.scenario?.dateOfBirth != null &&
      (this.persondob == null || this.persondob === '')
    ) {
      const invDobCopy = JSON.parse(
        JSON.stringify(this.investments?.calculator?.scenario?.dateOfBirth)
      );
      const tdate2 = new Date(invDobCopy);
      if (!isNaN(tdate2.getTime())) {
        this.persondob = format(tdate2, 'MMddyyyy');
      }
    }

    // 3rd Life Policy
    // Setting agentId
    if (this.currentPolicy != null) {
      //      this.scenario.agentId = this.currentPolicy?.agentId;

      // Looking at life policy to fill the dob if this.persondob is still empty
      if (this.currentPolicy.phases != null && (this.persondob == null || this.persondob === '')) {
        const phases = this.currentPolicy?.phases;
        phases.forEach((phase: IPhase | null) => {
          // Setting the persondob only if it's not null or empty string
          if (phase?.phaseNo === '01' && phase.dateOfBirth != null) {
            const lifeDobCopy = JSON.parse(JSON.stringify(phase.dateOfBirth));
            const tdate3 = new Date(lifeDobCopy);
            if (!isNaN(tdate3.getTime())) {
              this.persondob = format(tdate3, 'MMddyyyy');
            }
          }
        });
      }
    }
    // Other data besides DOB will be handled in pushDebts method in actions.js
    // Resetting the flag for $v for persondob
    // this.v$.persondob.$reset();
    window.scrollTo(0, 0);
  },
};

export default defineComponent(componentOptions);
</script>
<style lang="scss" scoped>
.mainContainerBackground {
  background-color: #ffffff;
}
.modal {
  position: fixed; /* Stay in place */
  z-index: 11; /* Sit on top */
  left: 0;
  top: 0;
  width: 100%; /* Full width */
  height: 100%; /* Full height */
  overflow: auto; /* Enable scroll if needed */
  background-color: rgb(0, 0, 0); /* Fallback color */
  background-color: rgba(0, 0, 0, 0.4); /* Black w/ opacity */
  padding-top: env(safe-area-inset-top); /* Ensures padding below the status bar/notch */
}
.dobinput {
  max-width: 300px;
}
</style>
